import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui';
import { apiFetch } from '../../api';

/**
 * ⌘K / Ctrl+K: jump to any page, run a global action, or find a user by name.
 *
 * `pages` and `actions` arrive already filtered for the signed-in role, so the
 * palette can never offer a page the router would refuse. User search uses the
 * same endpoint and matching as the Users page (`/api/users?search=`), and is
 * only offered when the role can open a user at all.
 *
 * Matching is a forgiving subsequence score: "brd" finds Broadcast, "dg" finds
 * Data governance. Keywords widen what a page answers to without renaming it.
 */

function score(query, text) {
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (!q) return 1;
  if (t.startsWith(q)) return 100 - t.length;
  const idx = t.indexOf(q);
  if (idx >= 0) return 60 - idx;
  let ti = 0;
  let run = 0;
  let total = 0;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found < 0) return 0;
    run = found === ti ? run + 1 : 0;
    total += 1 + run;
    ti = found + 1;
  }
  return total;
}

export default function CommandPalette({ open, onClose, pages, actions, canSearchUsers }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const returnFocus = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    returnFocus.current = document.activeElement;
    setQuery('');
    setUsers([]);
    setActive(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      const el = returnFocus.current;
      if (el && el.isConnected && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  // Debounced user lookup. A stale response is dropped by the `cancelled` flag.
  useEffect(() => {
    if (!open || !canSearchUsers || query.trim().length < 2) {
      setUsers([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: 1, limit: 6, search: query.trim() });
        const r = await apiFetch(`/api/users?${params}`);
        const data = r.ok ? await r.json() : { users: [] };
        if (!cancelled) setUsers(data.users || []);
      } catch (_) {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, query, canSearchUsers]);

  const groups = useMemo(() => {
    const rank = (items, text) => items
      .map((it) => ({ it, s: Math.max(score(query, text(it)), ...(it.keywords || []).map((k) => score(query, k) * 0.8)) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.it);
    const out = [];
    const p = rank(pages, (x) => `${x.label} ${x.section || ''}`);
    if (p.length) out.push({ label: 'Pages', items: p.map((x) => ({ ...x, kind: 'page' })) });
    const a = rank(actions, (x) => x.label);
    if (a.length) out.push({ label: 'Actions', items: a.map((x) => ({ ...x, kind: 'action' })) });
    if (users.length) {
      out.push({
        label: 'Users',
        items: users.map((u) => ({
          kind: 'user',
          id: `user-${u.id}`,
          label: u.username,
          hint: [u.tier_name, u.container_status].filter(Boolean).join(' · '),
          icon: 'user',
          to: `/users/${u.id}`,
        })),
      });
    }
    return out;
  }, [pages, actions, users, query]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => { setActive(0); }, [query]);
  useEffect(() => {
    if (active >= flat.length) setActive(Math.max(0, flat.length - 1));
  }, [flat.length, active]);

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const run = (item) => {
    if (!item) return;
    onClose();
    if (item.to) navigate(item.to);
    else if (item.run) item.run();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (flat.length ? (i + 1) % flat.length : 0)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(flat[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let index = -1;
  return (
    <div className="cmdk-overlay" onMouseDown={onClose}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Search and jump"
           onMouseDown={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
        <div className="cmdk-input-row">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            // Focus on mount, synchronously: a deferred focus drops the first
            // keystrokes of anyone who types straight after the shortcut.
            autoFocus
            className="cmdk-input"
            placeholder={canSearchUsers ? 'Jump to a page, run an action or find a user…' : 'Jump to a page or run an action…'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={flat[active] ? `cmdk-${flat[active].kind}-${flat[active].id || flat[active].to || flat[active].label}` : undefined}
            spellCheck={false}
            autoComplete="off"
          />
          {searching && <span className="spinner-sm" aria-label="Searching" />}
        </div>
        <div className="cmdk-list" id="cmdk-list" role="listbox" ref={listRef}>
          {flat.length === 0 && !searching && (
            <div className="cmdk-empty">Nothing matches “{query}”</div>
          )}
          {groups.map((g) => (
            <div key={g.label} role="group" aria-label={g.label}>
              <div className="cmdk-group-label">{g.label}</div>
              {g.items.map((item) => {
                index += 1;
                const i = index;
                const id = `cmdk-${item.kind}-${item.id || item.to || item.label}`;
                return (
                  <div
                    key={id}
                    id={id}
                    role="option"
                    aria-selected={i === active}
                    className="cmdk-item"
                    onMouseMove={() => setActive(i)}
                    onClick={() => run(item)}
                  >
                    {item.icon && <Icon name={item.icon} />}
                    <span className="cmdk-item-label">{item.label}</span>
                    {(item.hint || item.section) && <span className="cmdk-item-hint">{item.hint || item.section}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cmdk-footer" aria-hidden="true">
          <span><kbd className="kbd">↑</kbd><kbd className="kbd">↓</kbd> move</span>
          <span><kbd className="kbd">↵</kbd> open</span>
          <span><kbd className="kbd">Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
